import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  Alert,
  Box,
  Divider,
  CircularProgress,
  TextField,
  useMediaQuery,
  Chip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Collapse,
  IconButton,
  Tooltip,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Stack,
  Paper,
  ButtonGroup
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// Icons
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LinkIcon from "@mui/icons-material/Link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import HandshakeIcon from '@mui/icons-material/Handshake';
import DescriptionIcon from '@mui/icons-material/Description';
import PlatformIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Hooks (replace with your actual imports)
import { useMutation } from "react-query";
import useAuth from '../Hooks/use-auth';
import { useCreatorAuth } from '../Hooks/creator-use-auth';
import client from '../API';
import { replaceConsoleError } from '../Utils/toastNotifications';

/**
 * COMPLETELY REBUILT - MOBILE-FIRST OFFER DIALOG
 * Every element guaranteed visible and usable on mobile
 */
const UnifiedViewOfferDialog = ({
  open,
  onClose,
  deliverable,
  onUpdateDeliverable,
  onSendMessage,
  onOfferUpdated,
  conversationId,
  // Optional role override props
  isCreator: propIsCreator,
  userInfo: propUserInfo,
  creatorToken: propCreatorToken
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  // Hooks for user detection
  const { getCurrrentUser } = useAuth();
  const { creatorToken } = useCreatorAuth();
  
  // Auto-detect user type if not explicitly provided
  const userInfo = propUserInfo || getCurrrentUser();
  const detectedCreatorToken = propCreatorToken || creatorToken;
  const detectedIsCreator = propIsCreator !== undefined ? propIsCreator : 
    (!!detectedCreatorToken && !userInfo);

  // State management
  const [status, setStatus] = useState(deliverable?.status?.toLowerCase() || '');
  const [amount, setAmount] = useState(deliverable?.amount || '');
  const [revision, setRevision] = useState(deliverable?.revision || 0);
  const [initialStatus, setInitialStatus] = useState(deliverable?.status?.toLowerCase() || '');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showMilestones, setShowMilestones] = useState(false);
  const [showNegotiationForm, setShowNegotiationForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [daysSinceSent, setDaysSinceSent] = useState(0);

  // Derived state
  const hasStatusChanged = status !== initialStatus;
  const currentStatus = deliverable?.status?.toLowerCase() || '';

  // Access control logic based on role and current status
  const accessControl = useMemo(() => {
    const lockedStatuses = ["completed", "paid"];
    const isOfferLocked = lockedStatuses.includes(currentStatus);
    
    if (detectedIsCreator) {
      // Creator permissions
      const canRespondToOffers = !isOfferLocked && ["pending", "sent", "negotiate"].includes(currentStatus);
      const canCounterNegotiate = !isOfferLocked && currentStatus === "counter-negotiate";
      
      return {
        canChangeStatus: canRespondToOffers || canCounterNegotiate,
        canComplete: false,
        canNegotiate: canRespondToOffers || canCounterNegotiate,
        canAcceptDecline: canRespondToOffers,
        showStatusControls: canRespondToOffers || canCounterNegotiate,
        allowedActions: canRespondToOffers ? 
          ['accept', 'decline', 'counter-negotiate'] : 
          canCounterNegotiate ? ['counter-negotiate'] : [],
        userType: 'Creator'
      };
    } else {
      // Brand permissions - can respond to counter-offers from creators
      const canRespondToCounterOffer = !isOfferLocked && currentStatus === "counter-negotiate";
      
      return {
        canChangeStatus: canRespondToCounterOffer,
        canComplete: currentStatus === "accept",
        canNegotiate: canRespondToCounterOffer,
        canAcceptDecline: canRespondToCounterOffer, // Brands can accept/decline counter-offers
        showStatusControls: canRespondToCounterOffer,
        allowedActions: canRespondToCounterOffer ? ['accept', 'decline', 'counter-negotiate'] : [],
        userType: 'Brand'
      };
    }
  }, [detectedIsCreator, currentStatus]);

  // Calculate days since sent
  useEffect(() => {
    if (deliverable?.created_at) {
      const sentDate = new Date(deliverable.created_at);
      const currentDate = new Date();
      const differenceInDays = (currentDate - sentDate) / (1000 * 3600 * 24);
      setDaysSinceSent(Math.round(differenceInDays));
    }
  }, [deliverable]);

  // Reset state when deliverable changes
  useEffect(() => {
    if (deliverable) {
      const newStatus = deliverable.status?.toLowerCase() || '';
      setStatus(newStatus);
      setInitialStatus(newStatus);
      setAmount(deliverable.amount || '');
      setRevision(deliverable.revision || 0);
      setErrors({});
      setShowNegotiationForm(false);
    }
  }, [deliverable]);

  // Handle status changes and show/hide negotiation form
  useEffect(() => {
    if (status === "counter-negotiate") {
      setShowNegotiationForm(true);
    } else {
      setShowNegotiationForm(false);
    }
  }, [status]);

  // API mutations
  const updateOfferMutation = useMutation(
    async ({ deliverableId, updateData }) => {
      const response = await client.deliverables.update(deliverableId, updateData);
      return response;
    },
    {
      onMutate: () => setLoading(true),
      onSuccess: (data, variables) => {
        setShowSuccess(true);
        setLoading(false);

        let chatMsg = "";
        const newStat = variables.updateData.status;
        const userType = accessControl.userType;
        
        if (newStat === "accept") {
          chatMsg = `${userType} accepted the offer.`;
        } else if (newStat === "decline") {
          chatMsg = `${userType} declined the offer.`;
        } else if (newStat === "counter-negotiate") {
          chatMsg = `${userType} proposed a counter at $${variables.updateData.amount} with ${variables.updateData.revision} revisions.`;
        }

        if (conversationId && chatMsg) {
          client.conversations.sendMessage(conversationId, { message: chatMsg })
            .catch(error => replaceConsoleError("Error sending message", error));
        }

        if (onUpdateDeliverable) {
          onUpdateDeliverable(deliverable.id, variables.updateData);
        }
        if (onOfferUpdated) {
          onOfferUpdated(variables.updateData);
        }
        if (onSendMessage && chatMsg) {
          onSendMessage(chatMsg);
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      },
      onError: (error) => {
        replaceConsoleError("Error updating offer", error);
        setShowError(true);
        setErrorMessage(
          `Error updating offer: ${error?.response?.data?.error || error.message}`
        );
        setLoading(false);
      },
    }
  );

  const completeOfferMutation = useMutation(
    () => client.deliverables.update(deliverable.id, { status: "completed" }),
    {
      onMutate: () => setLoading(true),
      onSuccess: () => {
        setShowSuccess(true);
        setLoading(false);

        const updateData = { status: "completed" };
        const completionMessage = "Deliverable marked as completed! Payout has been processed.";

        if (conversationId) {
          client.conversations.sendMessage(conversationId, { message: completionMessage })
            .catch(error => replaceConsoleError("Error sending message", error));
        }

        if (onUpdateDeliverable) {
          onUpdateDeliverable(deliverable.id, updateData);
        }
        if (onOfferUpdated) {
          onOfferUpdated(updateData);
        }
        if (onSendMessage) {
          onSendMessage(completionMessage);
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      },
      onError: (error) => {
        replaceConsoleError("Error completing offer", error);
        setShowError(true);
        setErrorMessage(
          `Error completing offer: ${error?.response?.data?.error || error.message}`
        );
        setLoading(false);
      },
    }
  );

  // Event handlers
  const handleStatusChange = (newStatus) => {
    if (newStatus && accessControl.allowedActions.includes(newStatus)) {
      setStatus(newStatus);
      setErrors({});
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      if (errors.amount) {
        setErrors(prev => ({ ...prev, amount: null }));
      }
    }
  };

  const handleRevisionChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setRevision(value);
      if (errors.revision) {
        setErrors(prev => ({ ...prev, revision: null }));
      }
    }
  };

  const validateCounterOffer = () => {
    const newErrors = {};
    
    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount";
    }
    
    if (revision === "" || revision < 0) {
      newErrors.revision = "Please enter number of revisions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitStatus = () => {
    const updateData = { status };
    
    if (status === "counter-negotiate") {
      if (!validateCounterOffer()) {
        return;
      }
      updateData.amount = parseFloat(amount);
      updateData.revision = parseInt(revision, 10);
    }
    
    updateOfferMutation.mutate({
      deliverableId: deliverable.id,
      updateData,
    });
  };

  const handleCompleteOffer = () => {
    completeOfferMutation.mutate();
  };

  // Helper functions
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'accept':
      case 'accepted':
      case 'completed':
        return theme.palette.success.main;
      case 'pending':
      case 'sent':
        return theme.palette.info.main;
      case 'negotiate':
      case 'counter-negotiate':
        return theme.palette.warning.main;
      case 'decline':
      case 'declined':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'accept':
      case 'accepted':
      case 'completed':
        return <CheckCircleIcon />;
      case 'pending':
      case 'sent':
        return <PendingIcon />;
      case 'negotiate':
      case 'counter-negotiate':
        return <HandshakeIcon />;
      case 'decline':
      case 'declined':
        return <CancelIcon />;
      default:
        return <LocalOfferIcon />;
    }
  };

  const getStatusMessage = () => {
    if (!accessControl.showStatusControls) {
      const statusText = deliverable?.status || 'Unknown';
      if (currentStatus === 'completed') {
        return `This deliverable is currently ${statusText}. Payment may be processed.`;
      } else if (currentStatus === 'paid') {
        return `This deliverable is currently ${statusText}. This offer has been completed and paid.`;
      } else if (detectedIsCreator) {
        return `This deliverable is currently ${statusText}. No further changes available.`;
      } else {
        return `This deliverable is currently ${statusText}. Waiting for creator response.`;
      }
    }
    return null;
  };

  const dialogPalette = useMemo(
    () => ({
      canvas:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.default, 0.98)
          : "#f3efe7",
      surface:
        theme.palette.mode === "dark"
          ? theme.palette.background.paper
          : "#fffdf8",
      surfaceMuted:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.paper, 0.86)
          : "#f7f2e8",
      border:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.08)
          : alpha("#1f2937", 0.08),
      ink: theme.palette.text.primary,
      muted: theme.palette.text.secondary,
      accent: "#7a2e2a",
      accentSoft: alpha("#7a2e2a", 0.08),
      emerald: "#2f6f4f",
      emeraldSoft: alpha("#2f6f4f", 0.12),
      amberSoft: alpha(theme.palette.warning.main, 0.14),
      redSoft: alpha(theme.palette.error.main, 0.12),
      shadow:
        theme.palette.mode === "dark"
          ? "0 24px 64px rgba(0,0,0,0.42)"
          : "0 28px 70px rgba(28, 39, 56, 0.16)",
    }),
    [theme]
  );

  const primaryActionColor =
    status === "decline"
      ? theme.palette.error.main
      : status === "counter-negotiate"
      ? theme.palette.warning.main
      : dialogPalette.accent;

  const detailCardSx = {
    height: "100%",
    p: 1.75,
    borderRadius: 3,
    border: `1px solid ${alpha("#1f2937", theme.palette.mode === "dark" ? 0.16 : 0.12)}`,
    bgcolor: dialogPalette.surface,
    boxShadow:
      theme.palette.mode === "dark"
        ? "inset 0 1px 0 rgba(255,255,255,0.02), 0 1px 2px rgba(0,0,0,0.18)"
        : "0 1px 2px rgba(15, 23, 42, 0.04)",
  };

  // Offer details configuration
  const offerDetails = [
    { 
      label: "Type", 
      value: deliverable?.deliverable_type || "N/A",
      icon: <DescriptionIcon fontSize="small" />
    },
    {
      label: "Amount",
      value: `$${deliverable?.amount ? parseFloat(deliverable.amount).toFixed(2) : "0.00"}`,
      icon: <MonetizationOnIcon fontSize="small" />
    },
    {
      label: "Due Date",
      value: deliverable?.due_date
        ? new Date(deliverable.due_date).toLocaleDateString()
        : "N/A",
      icon: <CalendarTodayIcon fontSize="small" />
    },
    {
      label: "Revisions",
      value: deliverable?.revision != null ? deliverable.revision : "N/A",
      icon: <EditIcon fontSize="small" />
    },
    {
      label: "Platforms",
      value: deliverable?.platforms
        ? Array.isArray(deliverable.platforms)
          ? deliverable.platforms.join(", ")
          : deliverable.platforms
        : "N/A",
      icon: <PlatformIcon fontSize="small" />
    },
    { 
      label: "Days Since Sent", 
      value: daysSinceSent,
      icon: <CalendarTodayIcon fontSize="small" />
    },
  ];

  // Render milestones
  const renderMilestones = () => {
    if (!deliverable?.milestones || deliverable.milestones.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No milestones defined for this offer.
        </Typography>
      );
    }
    
    return deliverable.milestones.map((milestone, idx) => (
      <Card
        key={idx}
        variant="outlined"
        sx={{
          mb: 2,
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {milestone.description}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Due: {new Date(milestone.due_date).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Amount: ${parseFloat(milestone.amount).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
          {milestone.link && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <LinkIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                <a 
                  href={milestone.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: theme.palette.primary.main,
                    textDecoration: 'none'
                  }}
                >
                  {milestone.link}
                </a>
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    ));
  };

  if (!deliverable) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            borderRadius: isMobile ? 0 : 4,
            maxHeight: isMobile ? "100vh" : "92vh",
            height: isMobile ? "100vh" : "auto",
            maxWidth: isMobile ? "100%" : 920,
            overflow: "hidden",
            backgroundColor: dialogPalette.canvas,
            backgroundImage:
              theme.palette.mode === "dark"
                ? "none"
                : "linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(243,239,231,1) 100%)",
            boxShadow: dialogPalette.shadow,
            '& .MuiDialogContent-root': {
              scrollbarGutter: 'stable both-edges',
            },
          } 
        }}
      >
        {/* HEADER */}
        <DialogTitle
          sx={{
            bgcolor: dialogPalette.surface,
            borderBottom: `1px solid ${dialogPalette.border}`,
            p: { xs: 1.75, sm: 2.25 },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  bgcolor: alpha(getStatusColor(deliverable.status), 0.14),
                  color: getStatusColor(deliverable.status),
                  width: 52,
                  height: 52,
                }}
              >
                {getStatusIcon(deliverable.status)}
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ fontSize: { xs: "1.18rem", sm: "1.35rem" } }}
                >
                  {detectedIsCreator ? 'Review Offer' : 'Manage Offer'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {deliverable?.deliverable_type || 'Deliverable'} • #{deliverable?.id || 'N/A'}
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                icon={getStatusIcon(deliverable.status)}
                label={deliverable.status?.toUpperCase() || "UNKNOWN"}
                sx={{ 
                  bgcolor: alpha(getStatusColor(deliverable.status), 0.14),
                  color: getStatusColor(deliverable.status),
                  fontWeight: 700,
                  borderRadius: 999,
                  border: `1px solid ${alpha(getStatusColor(deliverable.status), 0.18)}`
                }}
              />
              <IconButton
                onClick={onClose}
                sx={{
                  bgcolor: dialogPalette.surfaceMuted,
                  border: `1px solid ${dialogPalette.border}`,
                  "&:hover": { bgcolor: alpha(dialogPalette.accent, 0.08) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        {/* CONTENT */}
        <DialogContent
          sx={{
            p: { xs: 2.25, sm: 2.5 },
            bgcolor: "transparent",
            overflowY: "auto",
            flex: 1
          }}
        >
          <Card
            sx={{
              mb: 3,
              borderRadius: 4,
              border: `1px solid ${alpha("#1f2937", theme.palette.mode === "dark" ? 0.14 : 0.1)}`,
              bgcolor: dialogPalette.surface,
              boxShadow: "none",
              overflow: "hidden",
              mt: 0.5,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box
                sx={{
                  mb: 2.5,
                  p: { xs: 1.6, sm: 2 },
                  borderRadius: 3,
                  color: dialogPalette.ink,
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(dialogPalette.accent, 0.18)} 0%, ${alpha(dialogPalette.emerald, 0.12)} 100%)`
                      : `linear-gradient(135deg, ${alpha(dialogPalette.accent, 0.08)} 0%, ${alpha(dialogPalette.emerald, 0.1)} 100%)`,
                  border: `1px solid ${dialogPalette.border}`,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: "0.12em", color: dialogPalette.muted }}>
                      Offer Snapshot
                    </Typography>
                    <Typography variant="h3" sx={{ fontSize: { xs: "1.65rem", sm: "2rem" }, fontWeight: 800, lineHeight: 1 }}>
                      ${deliverable?.amount ? parseFloat(deliverable.amount).toFixed(2) : "0.00"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: dialogPalette.muted, mt: 0.6, fontSize: '0.95rem' }}>
                      {deliverable?.deliverable_type || "Deliverable"} for {deliverable?.platforms
                        ? Array.isArray(deliverable.platforms)
                          ? deliverable.platforms.join(", ")
                          : deliverable.platforms
                        : "N/A"}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={`Due ${deliverable?.due_date ? new Date(deliverable.due_date).toLocaleDateString() : "N/A"}`}
                      sx={{ bgcolor: dialogPalette.surface, border: `1px solid ${dialogPalette.border}`, fontWeight: 600, height: 36 }}
                    />
                    <Chip
                      label={`${daysSinceSent} day${daysSinceSent === 1 ? "" : "s"} since sent`}
                      sx={{ bgcolor: dialogPalette.surface, border: `1px solid ${dialogPalette.border}`, fontWeight: 600, height: 36 }}
                    />
                  </Stack>
                </Box>
              </Box>

              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 1.75, display: 'flex', alignItems: 'center', gap: 1.25 }}
              >
                <InfoOutlinedIcon sx={{ color: dialogPalette.accent }} />
                Offer Summary
              </Typography>
              
              <Grid container spacing={1.75}>
                {offerDetails.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={detailCardSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 54 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: index % 2 === 0 ? dialogPalette.accentSoft : dialogPalette.emeraldSoft,
                            color: index % 2 === 0 ? dialogPalette.accent : dialogPalette.emerald,
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.2, lineHeight: 1.1 }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body1" fontWeight={700} sx={{ wordBreak: "break-word", lineHeight: 1.25, fontSize: '0.98rem' }}>
                            {item.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {(deliverable.notes || deliverable.script) && (
                <Grid container spacing={1.75} sx={{ mt: 0.75 }}>
                  {deliverable.notes && (
                    <Grid item xs={12} md={deliverable.script ? 6 : 12}>
                      <Box sx={{ ...detailCardSx, bgcolor: theme.palette.mode === "dark" ? alpha("#b08968", 0.12) : "#f4e8d8", border: `1px solid ${alpha("#a16207", 0.16)}` }}>
                        <Typography variant="overline" sx={{ color: dialogPalette.muted, letterSpacing: "0.08em" }}>
                          Brief Description
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1, lineHeight: 1.7 }}>
                          {deliverable.notes}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {deliverable.script && (
                    <Grid item xs={12} md={deliverable.notes ? 6 : 12}>
                      <Box sx={{ ...detailCardSx, bgcolor: dialogPalette.surfaceMuted, border: `1px solid ${alpha("#1f2937", theme.palette.mode === "dark" ? 0.16 : 0.12)}` }}>
                        <Typography variant="overline" sx={{ color: dialogPalette.muted, letterSpacing: "0.08em" }}>
                          Script Requirements
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1, lineHeight: 1.7 }}>
                          {deliverable.script}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* MILESTONES */}
          {deliverable.milestones && deliverable.milestones.length > 0 && (
            <Card
              sx={{
                mb: 3,
                borderRadius: 4,
                border: `1px solid ${alpha("#1f2937", theme.palette.mode === "dark" ? 0.14 : 0.1)}`,
                bgcolor: dialogPalette.surface,
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setShowMilestones(!showMilestones)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: dialogPalette.emeraldSoft,
                        color: dialogPalette.emerald,
                      }}
                    >
                      <TaskAltIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700}>
                      Milestones & To-Do List
                    </Typography>
                    <Chip
                      label={deliverable.milestones.length}
                      size="small"
                      sx={{ bgcolor: dialogPalette.surfaceMuted, fontWeight: 700 }}
                    />
                  </Box>
                  <IconButton sx={{ bgcolor: dialogPalette.surfaceMuted }}>
                    <ExpandMoreIcon sx={{ transform: showMilestones ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </IconButton>
                </Box>
                
                <Collapse in={showMilestones}>
                  <Box sx={{ mt: 2 }}>
                    {renderMilestones()}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* STATUS CONTROLS - MOBILE FIRST, BULLETPROOF */}
          {!accessControl.showStatusControls && (
            <Alert
              severity="info"
              sx={{
                borderRadius: 3,
                border: `1px solid ${dialogPalette.border}`,
                bgcolor: dialogPalette.surface,
              }}
            >
              {getStatusMessage()}
            </Alert>
          )}

          {accessControl.showStatusControls && (
            <Card
              sx={{
                borderRadius: 4,
                border: `1px solid ${alpha("#1f2937", theme.palette.mode === "dark" ? 0.14 : 0.1)}`,
                bgcolor: dialogPalette.surface,
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ p: { xs: 2.25, sm: 3.25 } }}>
                {/* TITLE */}
                <Typography variant="h5" fontWeight={700} textAlign="center" sx={{ mb: 1.5 }}>
                  {detectedIsCreator ? 'Choose Your Response' : 'Update Offer'}
                </Typography>
                
                {detectedIsCreator && (
                  <Alert severity="info" sx={{ mb: 4, borderRadius: 3 }}>
                    Review the offer details and select your response below.
                  </Alert>
                )}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                    gap: 1.5,
                    mb: 4,
                  }}
                >
                  {/* ACCEPT BUTTON */}
                  {accessControl.allowedActions.includes('accept') && (
                    <Button
                      variant={status === 'accept' ? 'contained' : 'outlined'}
                      onClick={() => handleStatusChange('accept')}
                      startIcon={<CheckCircleIcon />}
                      sx={{
                        py: 1.35,
                        fontSize: '0.98rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 3,
                        minHeight: 56,
                        color: status === 'accept' ? '#fff' : theme.palette.success.main,
                        bgcolor: status === 'accept' ? theme.palette.success.main : alpha(theme.palette.success.main, 0.04),
                        borderColor: alpha(theme.palette.success.main, 0.22),
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: status === 'accept' ? theme.palette.success.main : alpha(theme.palette.success.main, 0.08),
                          borderColor: alpha(theme.palette.success.main, 0.28),
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Accept Offer
                    </Button>
                  )}
                  
                  {/* DECLINE BUTTON */}
                  {accessControl.allowedActions.includes('decline') && (
                    <Button
                      variant={status === 'decline' ? 'contained' : 'outlined'}
                      onClick={() => handleStatusChange('decline')}
                      startIcon={<CancelIcon />}
                      sx={{
                        py: 1.35,
                        fontSize: '0.98rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 3,
                        minHeight: 56,
                        color: status === 'decline' ? '#fff' : theme.palette.error.main,
                        bgcolor: status === 'decline' ? theme.palette.error.main : alpha(theme.palette.error.main, 0.04),
                        borderColor: alpha(theme.palette.error.main, 0.22),
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: status === 'decline' ? theme.palette.error.main : alpha(theme.palette.error.main, 0.08),
                          borderColor: alpha(theme.palette.error.main, 0.28),
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Decline Offer
                    </Button>
                  )}
                  
                  {/* NEGOTIATE BUTTON */}
                  {accessControl.allowedActions.includes('counter-negotiate') && (
                    <Button
                      variant={status === 'counter-negotiate' ? 'contained' : 'outlined'}
                      onClick={() => handleStatusChange('counter-negotiate')}
                      startIcon={<HandshakeIcon />}
                      endIcon={<KeyboardArrowDownIcon sx={{ transform: showNegotiationForm ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
                      sx={{
                        py: 1.35,
                        fontSize: '0.98rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 3,
                        minHeight: 56,
                        color: status === 'counter-negotiate' ? '#fff' : theme.palette.warning.dark,
                        bgcolor: status === 'counter-negotiate' ? theme.palette.warning.main : alpha(theme.palette.warning.main, 0.08),
                        borderColor: alpha(theme.palette.warning.main, 0.22),
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: status === 'counter-negotiate' ? theme.palette.warning.main : alpha(theme.palette.warning.main, 0.12),
                          borderColor: alpha(theme.palette.warning.main, 0.3),
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {detectedIsCreator ? 'Make Counter Offer' : 'Negotiate'}
                    </Button>
                  )}
                </Box>

                {/* CURRENT SELECTION INDICATOR */}
                {hasStatusChanged && (
                  <Alert 
                    severity={status === 'accept' ? 'success' : status === 'decline' ? 'error' : 'warning'}
                    sx={{ mb: 3, borderRadius: 3, fontSize: '0.9rem', fontWeight: 500 }}
                  >
                    {status === 'accept' && 'You selected: Accept Offer'}
                    {status === 'decline' && 'You selected: Decline Offer'}
                    {status === 'counter-negotiate' && 'You selected: Make Counter Offer'}
                  </Alert>
                )}

                {/* NEGOTIATION FORM - ALWAYS VISIBLE WHEN SELECTED */}
                {showNegotiationForm && (
                  <Card sx={{ 
                    bgcolor: dialogPalette.surfaceMuted,
                    border: `1px solid ${theme.palette.warning.main}`,
                    borderRadius: 3,
                    mb: 3
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: 'warning.main' }}>
                        Negotiation Terms
                      </Typography>
                      
                      {/* AMOUNT INPUT */}
                      <TextField
                        fullWidth
                        label="Your Proposed Amount"
                        value={amount}
                        onChange={handleAmountChange}
                        error={!!errors.amount}
                        helperText={errors.amount || "Enter your counter amount"}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          sx: { fontSize: '1.2rem' }
                        }}
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            minHeight: 60,
                            fontSize: '1.2rem'
                          }
                        }}
                      />
                      
                      {/* REVISIONS INPUT */}
                      <TextField
                        fullWidth
                        label="Number of Revisions"
                        value={revision}
                        onChange={handleRevisionChange}
                        error={!!errors.revision}
                        helperText={errors.revision || "How many revisions will you include?"}
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            minHeight: 60,
                            fontSize: '1.2rem'
                          }
                        }}
                      />
                      
                      {/* FEE INFO */}
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography fontWeight={600} sx={{ mb: 1 }}>
                          Fee Information
                        </Typography>
                        Platform fees may apply when payments are processed.
                        {amount && parseFloat(amount) > 0 && (
                          <Typography fontWeight={600} sx={{ mt: 1 }}>
                            Net amount after fees: ${(parseFloat(amount) * 0.95).toFixed(2)}
                          </Typography>
                        )}
                      </Alert>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}
        </DialogContent>

        {/* FOOTER ACTIONS */}
        <DialogActions
          sx={{
            bgcolor: dialogPalette.surface,
            borderTop: `1px solid ${dialogPalette.border}`,
            p: { xs: 2, sm: 3 },
            flexDirection: 'column',
            gap: 2
          }}
        >
          {/* SUBMIT/UPDATE BUTTONS */}
          {accessControl.showStatusControls && hasStatusChanged && (
            <Box sx={{ width: '100%' }}>
              <Button 
                fullWidth
                variant="contained" 
                size="large"
                disabled={loading} 
                onClick={handleSubmitStatus}
                sx={{
                  mb: 2,
                  py: 1.2,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 52,
                  bgcolor: primaryActionColor,
                  boxShadow: "none",
                  '&:hover': {
                    bgcolor: primaryActionColor,
                    filter: 'brightness(0.96)',
                    boxShadow: "none",
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Updating...
                  </>
                ) : (
                  detectedIsCreator ? 'Submit My Response' : 'Update Offer'
                )}
              </Button>
              
              <Button 
                fullWidth
                variant="outlined"
                onClick={onClose} 
                disabled={loading}
                sx={{
                  py: 1.1,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 999
                }}
              >
                Cancel
              </Button>
            </Box>
          )}

          {/* COMPLETE OFFER BUTTON */}
          {accessControl.canComplete && (
            <Box sx={{ width: "100%" }}>
              <Divider sx={{ mb: 2 }} />
              <Button 
                fullWidth
                onClick={handleCompleteOffer} 
                variant="contained" 
                color="success"
                size="large"
                disabled={loading} 
                startIcon={<CheckCircleOutlineIcon />}
                sx={{ 
                  py: 1.2,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 52,
                  boxShadow: "none",
                }}
              >
                {loading ? 'Processing...' : 'Mark as Completed'}
              </Button>
            </Box>
          )}

          {/* CLOSE BUTTON FOR READ-ONLY */}
          {!accessControl.showStatusControls && !accessControl.canComplete && (
            <Button 
              fullWidth
              onClick={onClose} 
              variant="outlined" 
              size="large"
              sx={{ 
                py: 1.15,
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 999,
                minHeight: 52
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* SUCCESS NOTIFICATION */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled">
          Offer updated successfully!
        </Alert>
      </Snackbar>

      {/* ERROR NOTIFICATION */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UnifiedViewOfferDialog;
